import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { ACCIDENTS } from "../data/accidents";
import { OHIO_CITIES } from "../data/content";
import {
  ArrowRight,
  Scale,
  ShieldCheck,
  HelpCircle,
  Building2,
  CheckCircle2,
  XCircle,
  MinusCircle,
  MapPin,
  Calculator,
  FileText,
  Gavel,
  Landmark,
  Clock,
  BadgeCheck,
  Sparkles,
  BookOpen,
} from "lucide-react";

const SITE_URL = "https://instabridgefunding.com";

/* ─────────────────────────────────────────────────────────────
 * CityGuide — flagship template used for every city page.
 *
 * Consumes `{ city, state }` from `/data/city-guides/index.js`.
 *   - City-level content: hero, snapshot, timeline, court, case-type headings,
 *     coverage, city FAQs.
 *   - State-level content: statute grid, full law detail, comparison table,
 *     case types (with optional city overrides), differentiators, HB 105, byline.
 *
 * All future UX improvements (testimonials, calculator inline, trust bar
 * enhancements, etc.) should be added HERE so every city inherits them at
 * once. Do not create city-specific variants of this component.
 * ─────────────────────────────────────────────────────────── */

// Shared ToC — order every city page renders in. State/city data are merged.
const TOC = [
  { id: "snapshot", label: "What plaintiffs receive" },
  { id: "process", label: "How funding works" },
  { id: "comparison", label: "Instabridge vs. other options" },
  { id: "timeline", label: "The case timeline" },
  { id: "court", label: "Local court" },
  { id: "statutes", label: "Statute quick-reference" },
  { id: "law", label: "State law in detail" },
  { id: "cases", label: "Case types we fund" },
  { id: "coverage", label: "Local coverage" },
  { id: "differentiators", label: "Why Instabridge" },
  { id: "faq", label: "Common questions" },
];

export default function CityGuide({ city, state }) {
  const [activeSection, setActiveSection] = useState(TOC[0].id);

  // Merge case-type list from state, applying any city overrides.
  const mergedCaseTypes = useMemo(() => {
    const overrides = city.case_types?.type_overrides || {};
    return state.case_types_base.types.map((t) => ({
      ...t,
      ...(overrides[t.slug] || {}),
    }));
  }, [city, state]);

  // Merge FAQ: city FAQs first (venue-specific), then state FAQs.
  const mergedFaqs = useMemo(
    () => [...(city.faqs || []), ...state.faqs],
    [city, state],
  );

  // Templated headings — resolve {city} placeholder from state templates.
  const cityLabel = titleCase(city.slug);
  const howHeading = template(state.how_it_works.heading_template, { city: cityLabel });
  const diffHeading = template(state.differentiators.heading_template, { city: cityLabel });
  const snapshotBody = template(state.snapshot_body_template, { county: city.county });

  // IntersectionObserver highlights the active ToC entry.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: city.meta.h1,
        description: city.meta.description,
        author: { "@type": "Organization", name: "Instabridge Funding editorial team", url: SITE_URL },
        ...(city.meta.reviewed_by_adam
          ? { reviewedBy: { "@type": "Person", name: "Adam Gurney", jobTitle: "Founder & Attorney" } }
          : {}),
        publisher: {
          "@type": "Organization",
          name: "Instabridge Funding",
          logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
        },
        datePublished: city.meta.date_modified,
        dateModified: city.meta.date_modified,
        inLanguage: "en-US",
        isAccessibleForFree: true,
        mainEntityOfPage: city.meta.canonical,
      },
      {
        "@type": "FAQPage",
        mainEntity: mergedFaqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: state.name, item: `${SITE_URL}/states/${state.slug}` },
          { "@type": "ListItem", position: 3, name: cityLabel, item: city.meta.canonical },
        ],
      },
    ],
  };

  useSEO(city.meta.title, city.meta.description, {
    canonical: city.meta.canonical,
    suffix: false,
    jsonLd,
  });

  const otherCities = OHIO_CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="min-h-screen bg-white" data-testid={`city-guide-${city.slug}`}>
      <Navbar />

      {/* Breadcrumb */}
      <div className="border-b border-[#E5E7EB] py-3">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-xs text-[#6B7280]">
          <Link to="/" className="hover:text-[#0A2540]">Home</Link>
          <span className="mx-2">/</span>
          <Link to={`/states/${state.slug}`} className="hover:text-[#0A2540]">{state.name}</Link>
          <span className="mx-2">/</span>
          <span className="text-[#0A2540]">{cityLabel}</span>
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F5F7FA] to-white pt-12 pb-14 lg:pt-16 lg:pb-20 border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <div className="lg:col-span-8">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A2540] text-white text-sm font-semibold shadow-md mb-6"
              data-testid={`${city.slug}-hero-ribbon`}
            >
              <ShieldCheck className="w-4 h-4 text-[#57C6FF]" />
              {city.hero.ribbon}
            </div>

            <div className="uppercase-label mb-4 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {city.hero.eyebrow}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.03]">
              <span className="font-semibold">{city.hero.headline_top}</span>
              <span className="block text-[#374151] text-2xl sm:text-3xl mt-3 font-light">
                {city.hero.headline_sub}
              </span>
            </h1>

            <p className="mt-6 text-lg text-[#1F2937] leading-relaxed max-w-2xl">
              {city.hero.lede_empathy}
            </p>
            <p className="mt-4 text-lg text-[#1F2937] leading-relaxed max-w-2xl font-medium">
              {city.hero.lede_promise}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-7 text-xs text-[#374151]">
              {state.trust_chips.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-full shadow-sm"
                >
                  <ShieldCheck className="w-3 h-3 text-[#0066FF]" /> {c}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/apply">
                <Button className="btn-primary-navy rounded-full h-12 px-6" data-testid={`${city.slug}-hero-apply`}>
                  Apply now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/tools/funding-cost-calculator">
                <Button variant="outline" className="rounded-full h-12 px-6" data-testid={`${city.slug}-hero-calc`}>
                  <Calculator className="w-4 h-4 mr-2" /> Estimate your cost
                </Button>
              </Link>
            </div>

            <div className="mt-7 pt-5 border-t border-[#E5E7EB] text-xs text-[#6B7280] flex flex-wrap gap-x-6 gap-y-1">
              <span>{state.byline.author_label}</span>
              {city.meta.reviewed_by_adam && <span>{state.byline.reviewer_label}</span>}
              <span>Last updated {city.meta.date_modified}</span>
            </div>
          </div>

          <div className="lg:col-span-4 lg:mt-0">
            <div className="bg-[#0A2540] rounded-2xl p-6 lg:p-7 text-white shadow-xl">
              <div className="uppercase-label !text-white/60 flex items-center gap-2 mb-5">
                <Sparkles className="w-3.5 h-3.5" /> Why Instabridge
              </div>
              <ul className="space-y-5">
                {city.hero.why_bar.map((w) => (
                  <li key={w.label} className="flex gap-3">
                    <BadgeCheck className="w-5 h-5 text-[#57C6FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-display font-semibold text-white leading-snug">{w.label}</div>
                      <div className="text-sm text-white/70 mt-1 leading-relaxed">{w.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
              <Link to="/apply" className="block mt-6">
                <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 w-full font-semibold">
                  Start your {cityLabel} application
                </Button>
              </Link>
              <div className="mt-3 text-center text-xs text-white/50">
                Free to apply · No credit check · 5 minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile ToC */}
      <nav
        aria-label="On this page (mobile)"
        className="lg:hidden border-b border-[#E5E7EB] bg-white/95 backdrop-blur sticky top-16 z-30 shadow-sm"
        data-testid={`${city.slug}-toc-mobile`}
      >
        <div className="overflow-x-auto scrollbar-hide">
          <ul className="flex gap-2 px-4 py-3 whitespace-nowrap">
            {TOC.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeSection === item.id
                      ? "bg-[#0A2540] text-white"
                      : "bg-[#F9FAFB] text-[#6B7280] hover:text-[#0A2540] border border-[#E5E7EB]"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Main content grid */}
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 py-12 lg:py-14">
        <article className="lg:col-span-8 space-y-14 min-w-0">
          {/* Snapshot */}
          <SectionAnchor id="snapshot">
            <SectionEyebrow icon={BookOpen}>Snapshot</SectionEyebrow>
            <SectionHeading>{city.snapshot.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{snapshotBody}</p>
            <p className="mt-4 text-[#374151] leading-relaxed">{state.snapshot_what_it_is_not}</p>
            {city.snapshot.inline_cta && <InlineCTA {...city.snapshot.inline_cta} />}
          </SectionAnchor>

          {/* How it works */}
          <SectionAnchor id="process">
            <SectionEyebrow icon={FileText}>The funding process</SectionEyebrow>
            <SectionHeading>{howHeading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{state.how_it_works.intro}</p>

            <ol className="mt-7 space-y-4">
              {state.how_it_works.steps.map((s) => (
                <li
                  key={s.n}
                  className="flex gap-4 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl transition-colors hover:border-[#0066FF]/40"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0A2540] text-white flex items-center justify-center font-display font-semibold">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-display font-semibold text-[#0A2540]">{s.title}</div>
                    <p className="text-sm text-[#374151] mt-2 leading-relaxed">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>

            <p className="mt-6 text-sm text-[#374151] leading-relaxed">{state.how_it_works.when_it_fits}</p>

            <div className="mt-6 p-5 bg-[#0066FF]/5 border-l-4 border-[#0066FF] rounded-r-lg">
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-[#0052CC] uppercase tracking-wider mb-1">
                    The Instabridge check
                  </div>
                  <p className="text-sm text-[#0A2540] leading-relaxed">{state.instabridge_note}</p>
                </div>
              </div>
            </div>

            <InlineCTA {...state.how_it_works.inline_cta} />
          </SectionAnchor>

          {/* Comparison */}
          <SectionAnchor id="comparison">
            <SectionEyebrow icon={Scale}>Compare your options</SectionEyebrow>
            <SectionHeading>{state.comparison.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{state.comparison.intro}</p>
            <ComparisonTable comparison={state.comparison} citySlug={city.slug} />
            <p className="mt-4 text-xs text-[#6B7280] italic leading-relaxed">{state.comparison.footnote}</p>
            <InlineCTA {...state.comparison.inline_cta} />
          </SectionAnchor>

          {/* Timeline (city-specific) */}
          <SectionAnchor id="timeline">
            <SectionEyebrow icon={Clock}>Case timeline</SectionEyebrow>
            <SectionHeading>{city.timeline.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{city.timeline.intro}</p>
            <CaseTimeline milestones={city.timeline.milestones} citySlug={city.slug} />
            <p className="mt-5 text-[10px] text-[#6B7280] leading-relaxed">{city.timeline.source_note}</p>
          </SectionAnchor>

          {/* Court (city-specific) */}
          <SectionAnchor id="court">
            <SectionEyebrow icon={Building2}>The local court</SectionEyebrow>
            <SectionHeading>{city.court.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{city.court.intro}</p>
            <div className="mt-8 space-y-7">
              {city.court.subsections.map((s) => (
                <div key={s.h3} className="pl-4 border-l-2 border-[#E5E7EB] hover:border-[#0066FF] transition-colors">
                  <h3 className="font-display text-lg font-semibold text-[#0A2540]">{s.h3}</h3>
                  <p className="mt-2 text-[#374151] leading-relaxed">{s.body}</p>
                  {s.source_ref && <p className="mt-2 text-[10px] text-[#6B7280]">Source: {s.source_ref}</p>}
                </div>
              ))}
            </div>
          </SectionAnchor>

          {/* Statute grid */}
          <SectionAnchor id="statutes">
            <SectionEyebrow icon={Gavel}>Statute quick-reference</SectionEyebrow>
            <SectionHeading>{state.statute_table.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{state.statute_table.intro}</p>
            <StatuteGrid rows={state.statute_table.rows} citySlug={city.slug} />
          </SectionAnchor>

          {/* Full state law */}
          <SectionAnchor id="law">
            <SectionEyebrow icon={Landmark}>{state.name} law in detail</SectionEyebrow>
            <SectionHeading>{state.ohio_law.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{state.ohio_law.intro}</p>
            <div className="mt-8 space-y-5">
              {state.ohio_law.items.map((item) => (
                <div key={item.h3} className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
                  <h3 className="font-display text-lg font-semibold text-[#0A2540] flex items-start gap-2">
                    <Scale className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
                    <span>{item.h3}</span>
                  </h3>
                  <p className="mt-3 text-sm text-[#374151] leading-relaxed">{item.body}</p>
                  <p className="mt-3 text-[10px] text-[#6B7280]">Source: {item.source_ref}</p>
                </div>
              ))}
            </div>
            <InlineCTA {...state.ohio_law.inline_cta} />
          </SectionAnchor>

          {/* Case types */}
          <SectionAnchor id="cases">
            <SectionEyebrow icon={FileText}>Case types</SectionEyebrow>
            <SectionHeading>{city.case_types.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{city.case_types.intro}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {mergedCaseTypes.map((t) => {
                const accident = ACCIDENTS.find((a) => a.slug === t.slug);
                return (
                  <Link
                    key={t.slug}
                    to={`/accidents/${t.slug}`}
                    data-testid={`${city.slug}-accident-link-${t.slug}`}
                    className="group block bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#0066FF] rounded-xl p-5 transition-all hover:shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display font-semibold text-[#0A2540]">{t.label}</div>
                        <p className="text-sm text-[#374151] mt-1.5 leading-relaxed">{t.body}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#0066FF] mt-1 flex-shrink-0" />
                    </div>
                    {accident && (
                      <div className="text-[11px] text-[#0066FF] mt-3 group-hover:underline font-medium">
                        {accident.label} funding guide →
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </SectionAnchor>

          {/* Coverage */}
          <SectionAnchor id="coverage">
            <SectionEyebrow icon={MapPin}>Local coverage</SectionEyebrow>
            <SectionHeading>{city.suburbs.heading}</SectionHeading>
            <p className="mt-5 text-[#374151] leading-relaxed">{city.suburbs.intro}</p>
            <p className="mt-3 text-sm text-[#6B7280] italic">{state.coverage_note}</p>
          </SectionAnchor>

          {/* Differentiators */}
          <SectionAnchor id="differentiators">
            <SectionEyebrow icon={Sparkles}>Why Instabridge</SectionEyebrow>
            <SectionHeading>{diffHeading}</SectionHeading>
            <div className="mt-7 space-y-4">
              {state.differentiators.items.map((d) => (
                <div key={d.h3} className="p-6 bg-white border border-[#E5E7EB] rounded-xl">
                  <h3 className="font-display text-lg font-semibold text-[#0A2540] flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-[#0066FF]" /> {d.h3}
                  </h3>
                  <p className="mt-3 text-sm text-[#374151] leading-relaxed">{d.body}</p>
                  {d.cta && (
                    <Link
                      to={d.cta.to}
                      className="mt-3 inline-flex items-center gap-1 text-sm text-[#0066FF] hover:underline font-medium"
                    >
                      {d.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </SectionAnchor>

          {/* FAQ (merged) */}
          <SectionAnchor id="faq">
            <SectionEyebrow icon={HelpCircle}>Common questions</SectionEyebrow>
            <SectionHeading>Questions {cityLabel} plaintiffs actually ask.</SectionHeading>
            <div className="mt-7 space-y-4">
              {mergedFaqs.map((f, i) => (
                <div
                  key={i}
                  className="p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl hover:border-[#0066FF]/40 transition-colors"
                  data-testid={`${city.slug}-faq-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-display font-semibold text-[#0A2540]">{f.q}</h3>
                      <p className="mt-2 text-sm text-[#374151] leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Link
              to="/faq"
              className="mt-6 inline-flex items-center gap-1 text-sm text-[#0066FF] hover:underline font-medium"
            >
              See the full FAQ <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </SectionAnchor>
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-6">
            <nav
              aria-label="On this page"
              className="hidden lg:block bg-white border border-[#E5E7EB] rounded-2xl p-6"
              data-testid={`${city.slug}-toc`}
            >
              <div className="uppercase-label mb-4">On this page</div>
              <ul className="space-y-1 text-sm">
                {TOC.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className={`block py-1.5 px-2 rounded-md transition-colors ${
                        activeSection === item.id
                          ? "bg-[#0066FF]/10 text-[#0052CC] font-semibold"
                          : "text-[#6B7280] hover:text-[#0A2540] hover:bg-[#F9FAFB]"
                      }`}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="bg-[#0A2540] text-white rounded-2xl p-6">
              <div className="uppercase-label !text-white/60 mb-3">{cityLabel} funding</div>
              <div className="font-display text-2xl font-semibold leading-tight">$500 – $25,000</div>
              <div className="text-xs text-white/60 mt-1">
                funded within 24 hours of attorney verification
              </div>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/60">Recourse</dt>
                  <dd className="font-medium">Non-recourse</dd>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/60">Credit check</dt>
                  <dd className="font-medium">None</dd>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <dt className="text-white/60">Cancellation</dt>
                  <dd className="font-medium text-right">
                    {state.regulatory_labels.cancellation_now}
                    <br />
                    <span className="text-[11px] text-white/60">
                      {state.regulatory_labels.cancellation_incoming}
                    </span>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-white/60">Governed by</dt>
                  <dd className="font-medium text-right">
                    {state.regulatory_labels.governed_by_primary}
                    <br />
                    <span className="text-[11px] text-white/60">
                      → {state.regulatory_labels.governed_by_incoming}
                    </span>
                  </dd>
                </div>
              </dl>
              <Link to="/apply" className="block mt-6">
                <Button
                  className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 w-full font-semibold"
                  data-testid={`${city.slug}-sidebar-apply`}
                >
                  Apply now
                </Button>
              </Link>
              <div className="mt-3 text-center text-[11px] text-white/50">
                Free · No obligation · 5 minutes
              </div>
              <Link
                to="/tools/funding-cost-calculator"
                className="mt-4 flex items-center justify-center gap-1.5 text-xs text-white/80 hover:text-white transition-colors"
              >
                <Calculator className="w-3.5 h-3.5" /> Or estimate your cost first
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Related resources */}
      <section className="py-16 bg-[#F9FAFB] border-t border-[#E5E7EB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionEyebrow icon={BookOpen}>Continue reading</SectionEyebrow>
          <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-semibold mt-2">
            Related resources
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mb-3">By case type</h3>
              <ul className="space-y-2 text-sm">
                {ACCIDENTS.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/accidents/${a.slug}`} className="text-[#0066FF] hover:underline">
                      {a.label} funding →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mb-3">Other {state.name} cities</h3>
              <ul className="space-y-2 text-sm">
                {otherCities.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/states/${state.slug}/${c.slug}`} className="text-[#0066FF] hover:underline">
                      {c.name} funding →
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to={`/states/${state.slug}`} className="text-[#0066FF] hover:underline font-medium">
                    All {state.name} pages →
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-[#0A2540] mb-3">Services & tools</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/pre-settlement" className="text-[#0066FF] hover:underline">Pre-settlement funding →</Link></li>
                <li><Link to="/services/medical-funding" className="text-[#0066FF] hover:underline">Medical funding →</Link></li>
                <li><Link to="/services/litigation-finance" className="text-[#0066FF] hover:underline">Litigation finance →</Link></li>
                <li><Link to="/tools/funding-cost-calculator" className="text-[#0066FF] hover:underline">Funding cost calculator →</Link></li>
                <li><Link to="/how-it-works" className="text-[#0066FF] hover:underline">How our funding works →</Link></li>
                <li><Link to="/faq" className="text-[#0066FF] hover:underline">Full FAQ →</Link></li>
                <li><Link to="/about" className="text-[#0066FF] hover:underline">About Instabridge →</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0066FF]/10 border border-[#0066FF]/25 text-[#0052CC] text-xs font-semibold uppercase tracking-wider mb-5">
            <ShieldCheck className="w-3.5 h-3.5" /> If you lose, you owe $0
          </div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-semibold">
            Ready to apply?
          </h2>
          <p className="mt-3 text-[#6B7280]">
            5 minutes. No obligation. Funding decisions in hours, wired within 24 hours of attorney verification.
          </p>
          <Link to="/apply" className="inline-block mt-8">
            <Button
              className="btn-primary-navy rounded-full h-12 px-8"
              data-testid={`${city.slug}-bottom-apply`}
            >
              Start your application <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ─────── Local building blocks ─────── */

function SectionAnchor({ id, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      {children}
    </section>
  );
}

function SectionEyebrow({ icon: Icon, children }) {
  return (
    <div className="uppercase-label flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-light tracking-tight leading-tight">
      {children}
    </h2>
  );
}

function InlineCTA({ to, label }) {
  if (!to || !label) return null;
  return (
    <div className="mt-5">
      <Link
        to={to}
        className="inline-flex items-center gap-1 text-sm text-[#0066FF] hover:underline font-medium"
      >
        {label} <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function ComparisonTable({ comparison, citySlug }) {
  return (
    <div className="mt-7" data-testid={`${citySlug}-comparison`}>
      <div className="overflow-x-auto rounded-xl border border-[#E5E7EB]">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <th className="text-left px-4 py-4 font-display font-semibold text-[#0A2540] text-xs uppercase tracking-wider">
                What matters
              </th>
              {comparison.columns.map((col) => (
                <th
                  key={col.key}
                  className={`text-left px-4 py-4 font-display font-semibold text-xs uppercase tracking-wider ${
                    col.highlight ? "bg-[#0066FF] text-white" : "text-[#0A2540]"
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparison.rows.map((row, idx) => (
              <tr key={row.criterion} className={idx % 2 === 0 ? "bg-white" : "bg-[#FAFBFC]"}>
                <td className="px-4 py-4 font-medium text-[#0A2540] align-top border-r border-[#E5E7EB]">
                  {row.criterion}
                </td>
                {comparison.columns.map((col) => {
                  const cell = row[col.key];
                  return (
                    <td
                      key={col.key}
                      className={`px-4 py-4 align-top ${col.highlight ? "bg-[#0066FF]/5" : ""}`}
                    >
                      <ComparisonCell value={cell.v} note={cell.note} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden text-center text-xs text-[#6B7280] mt-2 italic">
        ← Swipe to compare all four options →
      </div>
    </div>
  );
}

function ComparisonCell({ value, note }) {
  const normalized = (value || "").toLowerCase();
  const isPositive =
    normalized === "yes" || normalized === "none" || normalized.startsWith("8–15%");
  const isNegative =
    normalized === "no" ||
    normalized === "required" ||
    normalized.startsWith("often 20") ||
    normalized === "balance + payment history reported" ||
    normalized === "your credit limit";
  const Icon = isPositive ? CheckCircle2 : isNegative ? XCircle : MinusCircle;
  const iconColor = isPositive ? "text-[#10B981]" : isNegative ? "text-[#EF4444]" : "text-[#9CA3AF]";
  return (
    <div className="flex items-start gap-2">
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${iconColor}`} />
      <div>
        <div className="text-[#0A2540] font-medium leading-snug">{value}</div>
        {note && <div className="text-[11px] text-[#6B7280] leading-snug mt-0.5">{note}</div>}
      </div>
    </div>
  );
}

function CaseTimeline({ milestones, citySlug }) {
  return (
    <div className="mt-8" data-testid={`${citySlug}-timeline`}>
      <ol className="relative">
        <div className="absolute left-4 top-2 bottom-2 w-px bg-[#E5E7EB]" aria-hidden />
        {milestones.map((m, i) => (
          <li key={m.title} className="relative pl-14 pb-8 last:pb-0">
            <div className="absolute left-0 top-1 w-9 h-9 rounded-full bg-white border-2 border-[#0066FF] text-[#0066FF] flex items-center justify-center font-display font-semibold text-sm shadow-sm">
              {i + 1}
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#0066FF]/40 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="uppercase-label !text-[#0066FF]">{m.phase}</div>
                  <h3 className="font-display font-semibold text-[#0A2540] mt-1">{m.title}</h3>
                </div>
              </div>
              <p className="mt-3 text-sm text-[#374151] leading-relaxed">{m.body}</p>
              <div className="mt-4 flex items-start gap-2 p-3 bg-[#0066FF]/5 rounded-lg border-l-2 border-[#0066FF]">
                <Sparkles className="w-3.5 h-3.5 text-[#0066FF] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[#0A2540] leading-relaxed">
                  <span className="font-semibold text-[#0052CC]">Where funding fits: </span>
                  {m.funding_note}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StatuteGrid({ rows, citySlug }) {
  return (
    <div className="mt-7 grid sm:grid-cols-2 gap-4" data-testid={`${citySlug}-statute-grid`}>
      {rows.map((r) => (
        <div
          key={r.code}
          className="bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#0066FF]/40 transition-colors"
        >
          <div className="flex items-start gap-2">
            <Gavel className="w-4 h-4 text-[#0066FF] mt-1 flex-shrink-0" />
            <div>
              <div className="font-mono text-[11px] text-[#0052CC] font-semibold uppercase tracking-wider">
                {r.code}
              </div>
              <h3 className="font-display font-semibold text-[#0A2540] mt-1">{r.title}</h3>
            </div>
          </div>
          <p className="mt-3 text-sm text-[#374151] leading-relaxed">{r.effect}</p>
          <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-1">
              Why it matters
            </div>
            <p className="text-xs text-[#0A2540] leading-relaxed">{r.why_it_matters}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────── Utilities ─────── */

function template(str, vars) {
  return str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

function titleCase(slug) {
  return slug
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}
